import type { FC } from 'react';

import React from 'react';
import styled from 'styled-components';

import { formatAmount } from 'src/utils/money';
import { pluralCars } from 'src/utils/pluralCars';

import { media } from 'src/theme';

import { Stack } from 'src/components/themed/Stack';

/**
 * The columns of a catalog, mobile first, on the one breakpoint set of the tokens (`theme.ts`):
 * one column below `sm` — the phone layout stays as it was — then two, three and four.
 */
export const gridColumnsCss = (breakpoints: typeof media): string => `
  grid-template-columns: 1fr;

  ${breakpoints.sm} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${breakpoints.md} {
    grid-template-columns: repeat(3, 1fr);
  }

  ${breakpoints.lg} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

/** The cards of a feed — sale, rental, a dealer's page — in one grid. */
export const ListingGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.space.md}px;
  width: 100%;
  ${gridColumnsCss(media)}
`;

/**
 * A catalog screen's column inside the scroll container: as wide as the window up to a desktop
 * width, then centred. The padding stays the screens' own (`p={4}`, `pb={30}` — the bottom one
 * keeps the last row clear of the tab bar).
 */
export const CatalogPage = styled(Stack)`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
`;

/**
 * The catalog's first row: Buy / Rent, and the assistant's pill beside it at the far end (app#397).
 * One line at every width down to 320px: neither wraps under the other nor runs over it.
 */
export const CatalogTopRow = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space.xs}px;
`;

/**
 * Under a catalog's heading: the filters and the cars. From the `md` breakpoint the filters are a
 * column on the left and the cars fill the rest (spec §4.2) — the screen renders the panel there and
 * only there; below it the cars take the whole width and the filters wait in a sheet behind the
 * funnel.
 */
export const CatalogBody = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
  gap: ${({ theme }) => theme.space.lg}px;

  ${media.md} {
    grid-template-columns: 280px minmax(0, 1fr);
  }
`;

/** "14 cars" under a catalog's heading. The number is a number, so it takes the display face. */
export const ListingCount: FC<{ total: number }> = ({ total }) => {
  const phrase = pluralCars(total);
  const count = formatAmount(total);

  return (
    <Count>
      <CountNumber>{count}</CountNumber>
      {phrase.slice(count.length)}
    </Count>
  );
};

const Count = styled.div`
  font-family: ${({ theme }) => theme.fonts.text};
  font-size: ${({ theme }) => theme.typography.large.fontSize}px;
  line-height: ${({ theme }) => theme.typography.large.lineHeight}px;
  color: ${({ theme }) => theme.colors.lightWhite};
`;

const CountNumber = styled.span`
  font-family: ${({ theme }) => theme.fonts.display};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.white};
`;
